import {
  put, takeLatest, all, select, call,
} from 'redux-saga/effects';
import Auth from '@aws-amplify/auth';
import { delay } from 'redux-saga';
import * as I18n from 'circe/I18n';
import {
  initUserData,
  tokens,
  signedUp,
  logout,
  preferencesFetched,
} from 'circe/authentication/actions';

import { changeLocale } from 'circe/I18n/actions';
import { getUser, getPreferences } from 'circe/authentication/selectors';
import { initProfiles, updateProfileToken, loadAuthorities } from 'circe/authorization/actions';
import { SUBMIT_MSGS } from 'circe/forms/constants';
import { notifySubmitFailure, notifySubmitSuccess } from 'circe/forms/saga';
import { startSubmit, stopSubmit } from 'circe/forms/actions';
import { bootstrap } from 'circe/actions';
import { block as blockUI, unblock as unblockUI } from 'domain/layout/actions';
import { authClient, operatorClient } from 'domain/HttpClients';
import userSage from 'domain/fuse/auth/users/saga';
import {
  AUTHORITIES,
  CONFIRM_FORM_NAME,
  LOGIN_FORM_NAME,
  RESET_PASSWORD_FORM_NAME,
  FORGOT_PASSWORD_FORM_NAME,
  SIGNUP_FORM_NAME,
} from 'domain/fuse/auth/constants';

import {
  confirmSignup,
  resendVerficationCode,
  login,
  resetPassowrd,
  forgotPassword,
  signup,
} from 'domain/fuse/auth/actions';

import { saveUserInLocalStorage } from 'domain/fuse/auth/utils';

function* doBootstrap() {
  // TODO
  // We need to dispatch an action from AWS and listen to it here
  yield delay(1000); // There might be a better way instead of delaying
  const state = yield select();
  const isLoggedIn = state.circe.authentication.logged === true;
  if (!isLoggedIn) {
    yield put(loadAuthorities([AUTHORITIES.VISITOR]));
  }
}

function* doCustomAuth(action) {
  yield put(blockUI('LOAD_AUTHORITIES'));
  const { userData } = action.payload;

  const state = yield select();
  const isLoggedIn = state.circe.authentication.logged === true;
  if (!isLoggedIn) {
    yield put(loadAuthorities([AUTHORITIES.VISITOR]));
  }
  if (userData.justCraeted) return;
  // In case of drivers ( Signed up on mobile )
  // const isUser = userData.email || userData.phone_number;
  const isUser = userData.email;
  if (isLoggedIn && isUser) {
    yield doAuthCalls(userData);
  }
}

function* doAuthCalls(userData) {
  const {
    email,
    phone_number,
    name,
    tokens,
    given_name,
    family_name,
    accountType,
    signingup,
  } = userData;

  const isIndividiualUser = parseInt(userData['custom:isIndividual']) > 0;
  const companyName = userData['custom:company'];
  const companyCode = userData['custom:companyCode'];

  const defaultCountry = userData['custom:defaultCountry'];
  const firstName = given_name || name;
  const lastName = family_name || name;

  const postData = {
    email,
    phone: phone_number,
    firstName,
    accountType,
    lastName,
    defaultCountry,
    address: 'My address',
  };
  const headers = {
    Authorization: `Bearer ${tokens.idToken}`,
  };
  if (signingup) {
    yield authClient
      .Post('/api/self')
      .withData(postData)
      .withHeaders(headers)
      .send();
    // None individual users shouldn't have accounts
    if (!isIndividiualUser) {
      const accountData = {
        address: 'Address',
        bankName: 'Bank Name',
        iban: 'iBanNumber',
        name: companyName,
        accountType,
        phone: phone_number,
        defaultCountry,
        domain: SYSTEM_DOMAIN,
      };
      yield authClient
        .Post('/api/self/accounts')
        .withData(accountData)
        .withHeaders(headers)
        .send();
    } else {
      const params = {
        role: AUTHORITIES.SHIPPER,
        token: encodeURIComponent(companyCode || DOMAIN_TOKEN),
      };

      yield authClient
        .Post('/api/self/profiles')
        .withHeaders(headers)
        .withParams(params)
        .send();
    }

    yield delay(2000);
    const _profiles = yield authClient
      .Get('/api/self/profiles')
      .withHeaders(headers)
      .send();

    const profiles = _profiles.filter((profile) => profile.status !== 'SUSPENDED');

    if (profiles) {
      const profilesAsKey = profiles.reduce((newProfiles, profile) => {
        newProfiles[profile.profileId] = profile;
        return newProfiles;
      }, {});
      yield put(initProfiles(profilesAsKey));
      const authorities = profiles.map((profile) => profile.role);
      authorities.push(AUTHORITIES.AUTHENTICATED);
      yield put(loadAuthorities(authorities));
      yield (window.location.href = '/');
    }
  } else {
    const resp = yield authClient
      .Get('/api/self')
      .withHeaders(headers)
      .send();

    if (resp) {
      const _profiles = yield authClient
        .Get('/api/self/profiles')
        .withHeaders(headers)
        .send();

      const profiles = _profiles.filter((profile) => profile.status !== 'SUSPENDED');

      if (profiles) {
        const profilesAsKey = profiles.reduce((newProfiles, profile) => {
          newProfiles[profile.profileId] = profile;
          return newProfiles;
        }, {});
        yield put(initProfiles(profilesAsKey));
        const authorities = profiles.map((profile) => profile.role);
        authorities.push(AUTHORITIES.AUTHENTICATED);
        yield put(loadAuthorities(authorities));
      }
    } else {
      alert(
        I18n.str(
          'profileUnderReview',
          'Your profile is under review, we notified the admin about it and now you will be logged out',
        ),
      );
      yield put(logout());
    }
  }
  yield put(unblockUI('LOAD_AUTHORITIES'));
}

function* doConfirmSignup(context, action) {
  const {
    payload: { username, code },
  } = action;
  try {
    const response = yield Auth.confirmSignUp(username || propsUsername, code, {
      forceAliasCreation: true,
    });
    yield notifySubmitSuccess(CONFIRM_FORM_NAME, response, SUBMIT_MSGS.success);
    yield context.history.push('/login');
  } catch (e) {
    yield notifySubmitFailure({}, CONFIRM_FORM_NAME, SUBMIT_MSGS.error, SUBMIT_MSGS.failure, {
      code: I18n.str(
        'invalidVerificationCode',
        'Invalid verification code provided, please try again',
      ),
    });
  }
}

function* doLogin(context, action) {
  const {
    payload: {
      username, password, accountType, signingup,
    },
  } = action;
  try {
    yield put(startSubmit(LOGIN_FORM_NAME));
    const resp = yield Auth.signIn(username, password);
    const { signInUserSession, attributes } = resp;

    const currentTokens = {
      accessToken: signInUserSession.accessToken.jwtToken,
      refreshToken: signInUserSession.refreshToken.token,
      idToken: signInUserSession.idToken.jwtToken,
    };

    yield put(
      initUserData({
        ...attributes,
        accountType,
        tokens: currentTokens,
        signingup,
      }),
    );
    yield put(tokens(currentTokens));
    yield put(stopSubmit(LOGIN_FORM_NAME));
    yield saveUserInLocalStorage({ ...attributes, tokens: currentTokens });
  } catch (e) {
    yield put(unblockUI('LOAD_AUTHORITIES'));
    yield put(stopSubmit(LOGIN_FORM_NAME));
    if (e.code && (e.code === 'NotAuthorizedException' || e.code === 'UserNotFoundException')) {
      yield notifySubmitFailure(
        {},
        LOGIN_FORM_NAME,
        SUBMIT_MSGS.error,
        I18n.str('errorLogin', 'Incorrect username or password.'),
        {
          username: I18n.str('errorLogin', 'Incorrect username or password.'),
        },
      );
    }
    console.error('Error while trying to login ', e);
  }
}

function* doResendConfirmationCode(action) {
  try {
    const {
      payload: { username },
    } = action;
    yield Auth.resendSignUp(username);
  } catch (e) {
    console.error('Error resending verification code', e);
  }
}

function* doResetPassword(context, action) {
  try {
    const {
      payload: { username, code, password },
    } = action;

    const response = yield Auth.forgotPasswordSubmit(username, code, password);

    yield notifySubmitSuccess(RESET_PASSWORD_FORM_NAME, response, SUBMIT_MSGS.success);
    yield context.history.push(`/login?username=${username}`);
  } catch (e) {
    yield notifySubmitFailure(
      {},
      RESET_PASSWORD_FORM_NAME,
      SUBMIT_MSGS.error,
      SUBMIT_MSGS.failure,
      { code: I18n.str('CodeMismatchException', e.message) },
    );
    console.error('Error while reseting password', e);
  }
}

function* doForgetPassword(context, action) {
  try {
    const {
      payload: { username },
    } = action;

    const response = yield Auth.forgotPassword(username);

    yield context.history.push(`/reset-password?username=${username}`);
    yield notifySubmitSuccess(RESET_PASSWORD_FORM_NAME, response, SUBMIT_MSGS.success);
  } catch (e) {
    yield notifySubmitFailure(
      {},
      FORGOT_PASSWORD_FORM_NAME,
      SUBMIT_MSGS.error,
      I18n.str('userNotFound', "User doesn't exist "),
      { username: I18n.str('userNotFound', "User doesn't exist ") },
    );
  }
}

function* doSignup(context, action) {
  try {
    const {
      payload: { values },
    } = action;
    const {
      username,
      password,
      termsAndConditions,
      company,
      captcha,
      companyCode,
      individualUser,
      userType,
      accountType,
      ...rest
    } = values;
    yield put(blockUI('LOAD_AUTHORITIES'));

    if (userType == 'shippingBusiness') {
      const resp = yield operatorClient
        .Get(`/public-api/company/token/${encodeURIComponent(companyCode)}/validate`)
        .send();

      if (!resp) {
        throw new Error('companyCodeNotFound');
      }
    }
    const name = `${values.given_name} ${values.family_name}`;
    yield put(startSubmit(SIGNUP_FORM_NAME));
    const response = yield Auth.signUp({
      username,
      password,
      attributes: {
        ...rest,
        name,
        email: username,
        'custom:companyCode': companyCode || '',
        'custom:company': company || `${name}'s Company`,
        'custom:isIndividual': userType !== 'business' ? '1' : '0',
      },
      validationData: [], // optional
    });
    yield notifySubmitSuccess(
      SIGNUP_FORM_NAME,
      response,
      I18n.str('signedUpSuccessfully', 'Signed Up Successfully'),
    );
    yield put(stopSubmit(SIGNUP_FORM_NAME));
    yield put(login(username, password, accountType, true));
  } catch (err) {
    yield put(unblockUI('LOAD_AUTHORITIES'));
    yield put(stopSubmit(SIGNUP_FORM_NAME));
    if (
      err.message.includes('An account with the given email already exists')
      || err.code === 'UsernameExistsException'
    ) {
      yield notifySubmitFailure(
        {},
        SIGNUP_FORM_NAME,
        SUBMIT_MSGS.error,
        I18n.str('alreadyExists', 'Already exists'),
        {
          username: I18n.str('alreadyExists', 'Already exists'),
        },
      );
    } else if (
      err.message.includes(
        'Invalid phone number format.' || err.code === 'InvalidParameterException',
      )
    ) {
      yield notifySubmitFailure(
        {},
        SIGNUP_FORM_NAME,
        SUBMIT_MSGS.error,
        I18n.str('phoneNumberInvalid', 'Phone number format is invalid'),
        {
          phone_number: I18n.str('phoneNumberInvalid', 'Phone number format is invalid'),
        },
      );
    } else if (err.message.includes('companyCodeNotFound')) {
      yield notifySubmitFailure(
        {},
        SIGNUP_FORM_NAME,
        SUBMIT_MSGS.error,
        I18n.str('companyCodeNotFound', 'Company code not found.'),
        {
          companyCode: I18n.str('companyCodeNotFound', 'Company code not found.'),
        },
      );
    } else {
      console.warn('Unhandled signup error', err);
    }
  }
}

function* doFetchPreferences({ payload }) {
  const { userData } = payload;

  if (!userData.tokens) {
    return;
  }

  const headers = {
    Authorization: `Bearer ${userData.tokens.idToken}`,
  };
  try {
    const preferences = yield authClient
      .Get('/api/self/preferences')
      .withHeaders(headers)
      .send();

    yield put(preferencesFetched(preferences));
  } catch (e) {
    console.error(e);
  }
}

function* doSavePreferences({ payload }) {
  const { locale } = payload;

  const state = yield select();

  const user = getUser(state);

  if (!user || !user.tokens) {
    return;
  }

  const headers = {
    Authorization: `Bearer ${user.tokens.idToken}`,
  };

  const preferences = {
    ...getPreferences(state),
    locale,
  };
  yield authClient
    .Patch('/api/self/preferences')
    .withData(preferences)
    .withHeaders(headers)
    .send();
}

function* watcher(context) {
  yield all([
    takeLatest(initUserData().type, doCustomAuth),
    takeLatest(bootstrap().type, doBootstrap),
    takeLatest(confirmSignup().type, doConfirmSignup, context),
    takeLatest(resendVerficationCode().type, doResendConfirmationCode),
    takeLatest(login().type, doLogin, context),
    takeLatest(resetPassowrd().type, doResetPassword, context),
    takeLatest(forgotPassword().type, doForgetPassword, context),
    takeLatest(signup().type, doSignup, context),
    takeLatest(initUserData().type, doFetchPreferences),
    takeLatest(changeLocale().type, doSavePreferences),
    userSage(context),
  ]);
}

export default watcher;
